import React, { useState, useEffect } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard';

import Form from 'react-bootstrap/Form'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Container from 'react-bootstrap/Container'
import Button from 'react-bootstrap/Button'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Nav from 'react-bootstrap/Nav'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'
import Image from 'react-bootstrap/Image'
import ButtonGroup from 'react-bootstrap/ButtonGroup'

import AirTightness from './components/AirTightness'
import Roof from './components/Roof'
import Shading from './components/Shading'
import Slab from './components/Slab'
import ThermalBridge from './components/ThermalBridge'
import VentImp from './components/VentImp'
import Wall from './components/Wall'
import Window from './components/Window'
import DesiredSystemSelect from './components/DesiredSystemSelect'
import HVACSystem from './components/HVACSystem'

import Solution from './components/Solution'
import ErrorDialog from './components/ErrorDialog'
import ReferenceBuildingDetails from './components/ReferenceBuildingDetails'
import StepByStep from './components/StepByStep';

import { base_url, auth_headers } from './http-auth-data'
import './App.css';

// HAPPEN Colors
// #74c7d6
// #91dc5b
// #ffda44
// #a0a9ab
// #f5f5f6

const App = () => {

    // set to true to use the "simplified" ui for clients
    // WARNING!!! When deployng the simplified ui, change the homepage attribute in package.json to client-pos-ui
    const is_client_pos = false;

    // Climatic zone
    const [zone, setZone] = useState('W1S2');

    const [reference_building_code, setReference_building_code] = useState('SFH13');
    const [reference_buildings_details, setReference_buildings_details] = useState([]);

    // Is the reference building spinned? (bool)
    const [spinned, setSpinned] = useState(false);

    // Energy cost by country (AVG = average)
    const [country, setCountry] = useState('AVG');
    const [energy_costs, setEnergy_costs] = useState([]);

    const [interest_rate, setInterest_rate] = useState(3.5);
    const [inflation, setInflation] = useState(2.0);
    const [years, setYears] = useState(30);

    const [walls, setWalls] = useState([]);
    const [slabs, setSlabs] = useState([]);
    const [roofs, setRoofs] = useState([]);
    const [thermal_bridges, setThermal_bridges] = useState([]);
    const [vent_imps, setVent_imps] = useState([]);
    const [shadings, setShadings] = useState([]);
    const [windows, setWindows] = useState([]);
    const [air_tightness, setAir_tightness] = useState([]);

    const [current_heating_system, setCurrent_heating_system] = useState({ id: 0, code: "HC-PRES", description: "Current heating system", energy_vector: "Natural gas", technology: 'boiler', efficiency: 0.98, cop: 0, seer: 0, cost: 0, max_power: 25, both: false, combined_dhw: true });
    const [current_cooling_system, setCurrent_cooling_system] = useState({ id: 0, code: "HC-PRES", description: "Current cooling system", energy_vector: "Electricity", technology: 'heat pump', efficiency: 0, cop: 0, seer: 2.0, cost: 0, max_power: 15, both: false, combined_dhw: false });
    const [current_dhw_system, setCurrent_dhw_system] = useState({ id: 0, code: "DHW-PRES", description: "Current DHW system", energy_vector: "Natural gas", technology: 'boiler', efficiency: 0.98, cop: 0, seer: 0, cost: 0, max_power: 16.226, both: false, combined_dhw: true });
    const [desired_heating_system, setDesired_heating_system] = useState({ id: 0, code: "HC-DES", description: "Desired heating system", energy_vector: "Natural gas", technology: 'boiler', efficiency: 0.98, cop: 0, seer: 0, cost: 0, max_power: 25, both: false, combined_dhw: true });
    const [desired_cooling_system, setDesired_cooling_system] = useState({ id: 0, code: "HC-DES", description: "Desired cooling system", energy_vector: "Electricity", technology: 'heat pump', efficiency: 0, cop: 0, seer: 2.0, cost: 0, max_power: 15, both: false, combined_dhw: false });
    const [desired_dhw_system, setDesired_dhw_system] = useState({ id: 0, code: "DHW-DES", description: "Desired DHW system", energy_vector: "Natural gas", technology: 'boiler', efficiency: 0.98, cop: 0, seer: 0, cost: 0, max_power: 16.226, both: false, combined_dhw: true });

    const [desired_heating_system_id, setDesired_heating_system_id] = useState(0);
    const [desired_cooling_system_id, setDesired_cooling_system_id] = useState(0);
    const [desired_dhw_system_id, setDesired_dhw_system_id] = useState(0);

    // Until initial data is not received from the backend, render a spinner (bool)
    const [init_data_received, setInit_data_received] = useState(false);

    // Gathers the 12 computed solutions
    const [solutions, setSolutions] = useState(null);

    // Gathers the 12 step-by-step computed solutions
    const [step_by_step_solutions, setStep_by_step_solutions] = useState(null);

    // Tab currently visualized
    const [current_tab, setCurrent_tab] = useState("scenario");

    // Holds the index of the solution currently selected in the vertical list of the computed solutions
    const [current_solution, setCurrent_solution] = useState(0);

    // Disable the solutions tab until solutions are computed (bool)
    const [disable_solutions_tab, setDisable_solutions_tab] = useState(true);

    // Used to show a spinner until the backend returns the solutions (bool)
    const [computing_solutions, setComputing_solutions] = useState(false);

    // Does the desired HVAC system has a combined DHW? (bool)
    const [desired_combined_dhw, setDesiredCombined_dhw] = useState(false);

    // Does the desired HVAC system has a heat system combined with the cooling system? (bool)
    const [desired_heat_both, setDesiredHeat_both] = useState(false);

    // Does the current HVAC system has a combined DHW? (bool)
    const [current_combined_dhw, setCurrentCombined_dhw] = useState(false);

    // Does the current HVAC system has a heat system combined with the cooling system? (bool)
    const [current_heat_both, setCurrentHeat_both] = useState(false);

    // Do we need to show an error? (bool)
    const [show_error, setShowError] = useState(false);
    const [error_message, setErrorMessage] = useState('No errors!');
    const [error_title, setErrorTitle] = useState('No errors!');

    // Do we need to show an alert? (bool)
    const [show_alert, setShowAlert] = useState(false);
    const [alert_message, setAlertMessage] = useState("No alert!");

    const ShowAlert = (message) => {
        window.scrollTo(0, 0);
        setAlertMessage(message);
        setShowAlert(true)
        setTimeout(() => {
            setShowAlert(false)
        }, 2000);
    }

    const handleShowAlert = () => {
        setShowError(false); //close error dialog (if open)
        ShowAlert("State copied to clipboard");
    }

    const handleErrorClose = () => setShowError(false);

    const handleCurrentCombinedDHWChange = (e) => {
        let checked = e.target.checked;

        setCurrent_heating_system(prevSystem => {
            return { ...prevSystem, 'combined_dhw': checked };
        });

        setCurrent_dhw_system(prevSystem => {
            return { ...prevSystem, 'combined_dhw': checked };
        });

        setCurrentCombined_dhw(e.target.checked);
    }

    const handleCurrentHeatBothChange = (e) => {
        let checked = e.target.checked;

        setCurrent_heating_system(prevSystem => {
            return { ...prevSystem, 'both': checked };
        });

        setCurrent_cooling_system(prevSystem => {
            return { ...prevSystem, 'both': checked };
        });

        setCurrentHeat_both(e.target.checked);
    }

    const handleDesiredCombinedDHWChange = (e) => {
        setDesiredCombined_dhw(e.target.checked);
    }

    const handleDesiredHeatBothChange = (e) => {
        setDesiredHeat_both(e.target.checked);
    }

    const handleChangeZone = (e) => {
        setZone(e.target.value);
    }

    // Performs initialization tasks (i.e. requests all data used to
    // populate all the form's fields and menus and puts it in the state)
    // Upon receiving the data it sets the state variable init_data_received to true
    useEffect(() => {
        fetch(base_url + "/index.php?cmd=init", {
            credentials: "include",
            headers: auth_headers
        })
            .then(res => res.json())
            .then(
                (result) => {
                    setWalls(result.walls);
                    setWindows(result.windows);
                    setSlabs(result.slabs);
                    setRoofs(result.roofs);
                    setAir_tightness(result.air_tightness);
                    setVent_imps(result.vent_imps);
                    setThermal_bridges(result.thermal_bridges);
                    setShadings(result.shadings);
                    setReference_buildings_details(result.reference_buildings_details);
                    setEnergy_costs(result.energy_costs);
                    setInit_data_received(true);
                },
                (error) => {
                    console.log(error);
                }
            )
    }, []);

    const handleSlabsChange = (e, id) => {
        const value = e.target.value

        setSlabs(prevSlabs => {
            return prevSlabs.map((slab, idx) => {
                if (id === idx) {
                    return { ...prevSlabs[id], cost: value }
                } else {
                    return slab;
                }
            })
        });
    }

    const handleShadingsChange = (e, id) => {
        const value = e.target.value

        setShadings(prevShadings => {
            return prevShadings.map((shading, idx) => {
                if (id === idx) {
                    return { ...prevShadings[id], cost: value }
                } else {
                    return shading;
                }
            })
        });
    }

    const handleAirTightnessChange = (e, id) => {
        const value = e.target.value

        setAir_tightness(prevAirTightnesses => {
            return prevAirTightnesses.map((air_t, idx) => {
                if (id === idx) {
                    return { ...prevAirTightnesses[id], cost: value }
                } else {
                    return air_t;
                }
            })
        });
    }

    const handleVentImpsChange = (e, id) => {
        const value = e.target.value

        setVent_imps(prevVentImps => {
            return prevVentImps.map((vent_i, idx) => {
                if (id === idx) {
                    return { ...prevVentImps[id], cost: value }
                } else {
                    return vent_i;
                }
            })
        });
    }

    const handleThermalBridgesChange = (e, id) => {
        const value = e.target.value

        setThermal_bridges(prevTB => {
            return prevTB.map((tb, idx) => {
                if (id === idx) {
                    return { ...prevTB[id], cost: value }
                } else {
                    return tb;
                }
            })
        });
    }

    const handleRoofChange = (e, id) => {
        const value = e.target.value

        setRoofs(prevRoof => {
            return prevRoof.map((rf, idx) => {
                if (id === idx) {
                    return { ...prevRoof[id], cost: value }
                } else {
                    return rf;
                }
            })
        });
    }

    const handleWindowsChange = (e, id) => {
        const value = e.target.value

        setWindows(prevWin => {
            return prevWin.map((win, idx) => {
                if (id === idx) {
                    return { ...prevWin[id], cost: value }
                } else {
                    return win;
                }
            })
        });
    }

    const handleWallsChange = (e, id) => {
        const value = e.target.value

        setWalls(prevWall => {
            return prevWall.map((wl, idx) => {
                if (id === idx) {
                    return { ...prevWall[id], cost: value }
                } else {
                    return wl;
                }
            })
        });
    }

    const handleCountryChange = (e) => {
        setCountry(e.target.value);
        fetch(`${base_url}/index.php?cmd=get_prices&country=${e.target.value}`, {
            credentials: "include",
            headers: auth_headers
        })
            .then(response => response.json())
            .then(data => {
                setWalls(data.walls)
                setWindows(data.windows);
                setSlabs(data.slabs);
                setRoofs(data.roofs);
                setAir_tightness(data.air_tightness);
                setVent_imps(data.vent_imps);
                setShadings(data.shadings);
                
            })
    }

    const handleCurrentHeatingSystemChange = e => {
        const { name, value, type, checked } = e.target
        setCurrent_heating_system(prevSystem => {
            switch (type) {
                case "number":
                case "text":
                case "select-one":
                    return { ...prevSystem, [name]: value };
                case "checkbox":
                    return { ...prevSystem, [name]: checked };
                default:
                    console.log("Undefined Current heating system");
                    break;
            }
        });
    }
    const handleCurrentCoolingSystemChange = e => {
        const { name, value, type, checked } = e.target
        setCurrent_cooling_system(prevSystem => {
            switch (type) {
                case "number":
                case "select-one":
                case "text":
                    return { ...prevSystem, [name]: value };
                case "checkbox":
                    return { ...prevSystem, [name]: checked };
                default:
                    console.log("Undefined Current cooling system");
                    break;
            }
        });
    }

    const handleCurrentDHWSystemChange = e => {
        const { name, value, type, checked } = e.target
        setCurrent_dhw_system(prevSystem => {
            switch (type) {
                case "number":
                case "select-one":
                case "text":
                    return { ...prevSystem, [name]: value };
                case "checkbox":
                    return { ...prevSystem, [name]: checked };
                default:
                    console.log("Undefined Current DHW system");
                    break;
            }
        });
    }
    const handleDesiredHeatingSystemChange = e => {
        const { name, value, type, checked } = e.target
        setDesired_heating_system(prevSystem => {
            switch (type) {
                case "number":
                case "select-one":
                case "text":
                    return { ...prevSystem, [name]: value };
                case "checkbox":
                    return { ...prevSystem, [name]: checked };
                default:
                    console.log("Undefined Desired heating system");
                    break;
            }
        });
    }

    const handleDesiredCoolingSystemChange = e => {
        const { name, value, type, checked } = e.target
        setDesired_cooling_system(prevSystem => {
            switch (type) {
                case "number":
                case "select-one":
                case "text":
                    return { ...prevSystem, [name]: value };
                case "checkbox":
                    return { ...prevSystem, [name]: checked };
                default:
                    console.log("Undefined Desired cooling system");
                    break;
            }
        });
    }

    const handleDesiredDHWSystemChange = e => {
        const { name, value, type, checked } = e.target
        setDesired_dhw_system(prevSystem => {
            switch (type) {
                case "number":
                case "select-one":
                case "text":
                    return { ...prevSystem, [name]: value };
                case "checkbox":
                    return { ...prevSystem, [name]: checked };
                default:
                    console.log("Undefined Desired DHW system");
                    break;
            }
        });
    }

    const handleDesiredHeatingSystemSelectChange = e => {
        var hvac_id = e.target.value
        setDesired_heating_system_id(hvac_id);

        if (hvac_id === "0") {
            setDesired_heating_system(current_heating_system);
        } else {
            fetch(`${base_url}/index.php?cmd=fetchsystem&id=${hvac_id}&country=${country}`, {
                credentials: "include",
                headers: auth_headers
            })
                .then(response => response.json())
                .then(data => {
                    setDesired_heating_system(data);
                })
        }
    }

    const handleDesiredCoolingSystemSelectChange = e => {
        var hvac_id = e.target.value
        setDesired_cooling_system_id(hvac_id);

        if (hvac_id === "0") {
            setDesired_cooling_system(current_cooling_system);
        } else {
            fetch(`${base_url}/index.php?cmd=fetchsystem&id=${hvac_id}&country=${country}`, {
                credentials: "include",
                headers: auth_headers
            })
                .then(response => response.json())
                .then(data => {
                    setDesired_cooling_system(data);
                })
        }
    }

    const handleDesiredDHWSystemSelectChange = (e) => {
        var hvac_id = e.target.value
        setDesired_dhw_system_id(hvac_id);

        if (hvac_id === "0") {
            setDesired_dhw_system(current_dhw_system);
        } else {
            fetch(`${base_url}/index.php?cmd=fetchsystem&id=${hvac_id}&country=${country}`, {
                credentials: "include",
                headers: auth_headers
            })
                .then(response => response.json())
                .then(data => {
                    setDesired_dhw_system(data);
                })
        }
    }

    // When a tab is selected updates the current_tab state variable according to the
    // selected tab's name
    const handleSelectTab = (tab) => {
        setCurrent_tab(tab);
    }

    const renderWalls = () => {
        const walls_ids = [...Array(12).keys()];
        return (
            <div id="walls">
                <h2 className="text-center">Walls</h2>
                <Form.Row>
                    <Col xs={2} lg={1}></Col>
                    <Col xs={3} lg={1}>Cost (&euro;/m<sup>2</sup>)</Col>
                    <Col xs={2} lg={1}>R (m<sup>2</sup>K/W)</Col>
                </Form.Row>

                {walls_ids.map(value => {
                    return <Wall
                        key={value}
                        wall_id={value}
                        code={walls[value].code}
                        changeFunc={handleWallsChange}
                        cost={walls[value].cost}
                        r={walls[value].r}
                    />
                })}
            </div>
        );
    }

    const renderRoofs = () => {
        const roofs_ids = [...Array(11).keys()];
        return (
            <div id="roofs">
                <h2 className="text-center">Roof</h2>
                <Form.Row>
                    <Col xs={2} lg={1}></Col>
                    <Col xs={3} lg={1}>Cost (&euro;/m<sup>2</sup>)</Col>
                    <Col xs={2} lg={1}>R (m<sup>2</sup>K/W)</Col>
                </Form.Row>

                {roofs_ids.map(value => {
                    return <Roof
                        key={value}
                        roof_id={value}
                        code={roofs[value].code}
                        changeFunc={handleRoofChange}
                        cost={roofs[value].cost}
                        r={roofs[value].r}
                    />
                })}

            </div>
        );
    }

    const renderSlabs = () => {
        const slabs_ids = [...Array(10).keys()];
        return (
            <div id="slabs">
                <h2 className="text-center">Slabs</h2>
                <Form.Row>
                    <Col xs={2} lg={1}></Col>
                    <Col xs={3} lg={1}>Cost (&euro;/m<sup>2</sup>)</Col>
                    <Col xs={2} lg={1}>R (m<sup>2</sup>K/W)</Col>
                </Form.Row>

                {slabs_ids.map((value) => {
                    return <Slab
                        key={value}
                        slab_id={value}
                        code={slabs[value].code}
                        changeFunc={handleSlabsChange}
                        cost={slabs[value].cost}
                        r={slabs[value].r}
                    />
                })}

            </div>
        );
    }

    const renderVentImps = () => {
        const ventimps_ids = [...Array(3).keys()];
        return (
            <div id="vent_imps">
                <Form.Row>
                    <Col xs={12} lg={6}>
                        <h2 className="text-center">Vent Imps</h2>
                    </Col>
                </Form.Row>
                <Form.Row>
                    <Col xs={2} lg={2}></Col>
                    <Col xs={5} lg={2}>Cost (&euro;/100m<sup>2</sup>)</Col>
                    <Col xs={4} lg={2}>Eq. Air flow (n air change/h)</Col>
                </Form.Row>

                {ventimps_ids.map(value => {
                    return <VentImp
                        key={value}
                        vent_imp_id={value}
                        code={vent_imps[value].code}
                        changeFunc={handleVentImpsChange}
                        cost={vent_imps[value].cost}
                        equivalent_air_flow={vent_imps[value].equivalent_air_flow}
                    />
                })}

            </div>
        );
    }

    const renderAirTightnesses = () => {
        const airt_ids = [...Array(3).keys()];
        return (
            <div id="air_tightnesses">
                <Form.Row>
                    <Col xs={12} lg={6}>
                        <h2 className="text-center">Air Thighness</h2>
                    </Col>
                </Form.Row>
                <Form.Row>
                    <Col xs={2} lg={2}></Col>
                    <Col xs={5} lg={2}>Cost <br />(&euro;/m<sup>2</sup>)</Col>
                    <Col xs={4} lg={2}>n50</Col>
                </Form.Row>

                {airt_ids.map(value => {
                    return <AirTightness
                        key={value}
                        air_tightness_id={value}
                        code={air_tightness[value].code}
                        changeFunc={handleAirTightnessChange}
                        cost={air_tightness[value].cost}
                        n50={air_tightness[value].n50}
                    />
                })}

            </div>
        );
    }

    const renderThermalBridges = () => {
        const tb_ids = [...Array(3).keys()];
        return (
            <div id="thermal_bridges">
                <Form.Row>
                    <Col xs={12} lg={6}>
                        <h2 className="text-center">Thermal Bridge</h2>
                    </Col>
                </Form.Row>
                <Form.Row>
                    <Col xs={2} lg={2}></Col>
                    <Col xs={5} lg={2}>Cost (&euro;/m<sup>2</sup>)</Col>
                    <Col xs={4} lg={2}>Reduction</Col>
                </Form.Row>

                {tb_ids.map(value => {
                    return <ThermalBridge
                        key={value}
                        tb_id={value}
                        code={thermal_bridges[value].code}
                        changeFunc={handleThermalBridgesChange}
                        cost={thermal_bridges[value].cost}
                        thermal_bridge_reduction={thermal_bridges[value].thermal_bridge_reduction}
                    />
                })}

            </div>
        );
    }

    const renderWindows = () => {
        const windows_ids = [...Array(9).keys()];

        return (
            <div id="windows">
                <h2 className="text-center">Windows</h2>
                <Form.Row>
                    <Col xs={2} lg={1}></Col>
                    <Col xs={3} lg={1}>Cost (&euro;/m<sup>2</sup>)</Col>
                    <Col xs={2} lg={1}>g</Col>
                    <Col xs={2} lg={1}>Uw (W/m<sup>2</sup>K)</Col>
                    <Col xs={1} lg={1}>Uf (W/m<sup>2</sup>K)</Col>
                </Form.Row>

                {windows_ids.map(value => {
                    return <Window
                        key={value}
                        window_id={value}
                        code={windows[value].code}
                        changeFunc={handleWindowsChange}
                        cost={windows[value].cost}
                        solar_factor={windows[value].solar_factor}
                        uw={windows[value].uw}
                        uf={windows[value].uf}
                    />
                })}

            </div>
        );
    }

    const renderShadings = () => {
        const shadings_ids = [...Array(2).keys()];
        return (
            <div id="shadings">
                <Form.Row>
                    <Col xs={12} lg={6}>
                        <h2 className="text-center">Shadings</h2>
                    </Col>
                </Form.Row>
                <Form.Row>
                    <Col xs={2} lg={2}></Col>
                    <Col xs={5} lg={2}>Cost (&euro;/m<sup>2</sup>)</Col>
                    <Col xs={4} lg={2}>Solar factor</Col>
                </Form.Row>

                {shadings_ids.map(value => {
                    return <Shading
                        key={value}
                        shading_id={value}
                        code={shadings[value].code}
                        changeFunc={handleShadingsChange}
                        cost={shadings[value].cost}
                        solar_factor={shadings[value].solar_factor}
                    />
                })}

            </div>
        );
    }

    // Renders the solutions tab
    const renderSolutions = () => {
        if (solutions === null) {
            return (<Container className="d-flex flex-column min-vh-100">
                <Row className="flex-grow-1 justify-content-center align-items-center">
                    <Col></Col>
                    <Col>
                        <div className="d-flex justify-content-center">
                            <Spinner animation="border" role="status" className="happen-spinner">
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </div>
                    </Col>
                    <Col></Col>
                </Row >
            </Container>)
        }

        return (
            <Tab.Container
                id="solutions_tabs"
                defaultActiveKey="solution_1"
                activeKey={current_solution}
                onSelect={(k) => setCurrent_solution(k)}
            >
                <Row>
                    {
                        // Vertical navbar to select one of the 12 solutions
                    }
                    <Col sm={1}>
                        <Nav variant="pills" className="flex-column">
                            {solutions.map((sol, index) => {
                                return (
                                    <Nav.Item key={`solution_${index + 1}_key`}>
                                        <Nav.Link
                                            className="solution_tab"
                                            eventKey={index}
                                        >
                                            {`Sol. ${index + 1}`}
                                        </Nav.Link>
                                    </Nav.Item>
                                )
                            })}

                        </Nav>
                    </Col>
                    {
                        // 
                    }
                    <Col sm={11}>
                        <Tab.Content>
                            {solutions.map((sol, index) => {
                                return (
                                    <Tab.Pane
                                        key={`solution_${index + 1}_tp`}
                                        eventKey={index}
                                    >
                                        <Solution solution={sol} />
                                    </Tab.Pane>
                                )
                            })}
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        );
    }

    // Renders the step by step tab
    const renderStepByStep = () => {
        if (step_by_step_solutions === null) {
            return (<Container className="d-flex flex-column min-vh-100">
                <Row className="flex-grow-1 justify-content-center align-items-center">
                    <Col></Col>
                    <Col>
                        <div className="d-flex justify-content-center">
                            <Spinner animation="border" role="status" className="happen-spinner">
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </div>
                    </Col>
                    <Col></Col>
                </Row >
            </Container>);
        }

        return (
            <StepByStep
                step_by_step_solution={step_by_step_solutions[current_solution]}
                solution={solutions[current_solution]}
            />
        );
    }

    const stateToJson = () => {
        return JSON.stringify(
            {
                'zone': zone,
                'reference_building': reference_building_code,
                'spinned': spinned,
                'country': country,
                'inflation': inflation,
                'interest_rate': interest_rate,
                'years': years,
                'walls': walls,
                'slabs': slabs,
                'roofs': roofs,
                'thermal_bridges': thermal_bridges,
                'vent_imps': vent_imps,
                'shadings': shadings,
                'windows': windows,
                'air_tightness': air_tightness,
                'current_heating_system': current_heating_system,
                'current_cooling_system': current_cooling_system,
                'current_dhw_system': current_dhw_system,
                'desired_heating_system': desired_heating_system,
                'desired_cooling_system': desired_cooling_system,
                'desired_dhw_system': desired_dhw_system,
                'current_combined_dhw': current_combined_dhw,
                'current_heat_both': current_heat_both,
                'desired_combined_dhw': desired_combined_dhw,
                'desired_heat_both': desired_heat_both,
                'solutions': solutions,
                'step_by_step_solutions': step_by_step_solutions
            }
        );
    }

    // Callback associated to the "Compute" button
    const submitData = (e) => {
        e.preventDefault()

        setSolutions(null);
        setStep_by_step_solutions(null);
        setDisable_solutions_tab(true);
        setComputing_solutions(true);

        fetch(base_url + '/index.php?cmd=compute', {
            method: 'POST',
            credentials: "include",
            headers: auth_headers,
            body: stateToJson()
        })
            .then(response => response.json())
            .then(data => {

                setSolutions(data.solutions);
                setStep_by_step_solutions(data.step_by_step);
                setCurrent_tab("solutions");
                setDisable_solutions_tab(false);
                setComputing_solutions(false);
            })
            .catch((error) => {
                setErrorTitle("Error");
                setErrorMessage("An error occurred while computing the solutions");
                setShowError(true);
                setComputing_solutions(false);
            });

    }

    if (!init_data_received) {
        return (
            <Container className="d-flex flex-column min-vh-100">
                <Row className="flex-grow-1 justify-content-center align-items-center">
                    <Col></Col>
                    <Col>
                        <div className="d-flex justify-content-center">
                            <Spinner animation="border" role="status" className="happen-spinner">
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </div>
                    </Col>
                    <Col></Col>
                </Row >
            </Container>)
    }

    return (
        <Container fluid>
            <Alert show={show_alert} variant="success">
                {alert_message}
            </Alert>
            <ErrorDialog
                show={show_error}
                title={error_title}
                body={error_message}
                handleClose={handleErrorClose}
                handleCopyState={handleShowAlert}
                getJsonState={stateToJson}
            />
            <Form>
                <Tabs defaultActiveKey="scenario"
                    activeKey={current_tab}
                    id="uncontrolled-tab-example"
                    onSelect={handleSelectTab}
                    className="main_tabs"
                >
                    {
                        // Scenarios tab
                    }

                    <Tab eventKey="scenario" title="Scenario">
                        <h2 className="text-center">Scenario</h2>
                        <Container fluid>
                            <Row>
                                <Col xs={4} lg={2}>
                                    Climatic Zone: {zone}<br />
                                    <ButtonGroup vertical>
                                        <Button variant='w1s2' value='W1S2' onClick={handleChangeZone}>W1S2</Button>
                                        <Button variant='w2s2' value='W2S2' onClick={handleChangeZone}>W2S2</Button>
                                        <Button variant='w3s2' value='W3S2' onClick={handleChangeZone}>W3S2</Button>
                                        <Button variant='w2s3' value='W2S3' onClick={handleChangeZone}>W2S3</Button>
                                    </ButtonGroup>
                                </Col>
                                <Col m={4} lg={6}>
                                    <Image src={require('./assets/map.png')} fluid />
                                </Col>
                                <Col xs={4} lg={2}>
                                    <Form.Group>
                                        <Form.Label>Load costs for country</Form.Label>
                                        <Form.Control as="select"
                                            id='country'
                                            custom
                                            name="country"
                                            country={country}
                                            onChange={(e) => handleCountryChange(e)}
                                            defaultValue="AVG"
                                        >
                                            <option key='0' value='AVG'>Average</option>
                                            <option key='1' value='HR'>Croatia</option>
                                            <option key='2' value='CY'>Cyprus</option>
                                            <option key='3' value='FR'>France</option>
                                            <option key='4' value='GR'>Greece</option>
                                            <option key='5' value='IT'>Italy</option>
                                            <option key='6' value='SL'>Slovenia</option>
                                            <option key='7' value='SP'>Spain</option>

                                        </Form.Control>
                                    </Form.Group>
                                    <p>Natural gas: &nbsp;
                                        {energy_costs
                                            .filter((e) => (e.country === country) && (e.typology === 'Natural gas'))
                                            .map((e) => { 
                                                var avg_cost = energy_costs.filter((e) => (e.country === 'AVG') && (e.typology === 'Natural gas'))[0].price;
                                                return e.price === null ? avg_cost : e.price; 
                                            })
                                            .reduce((e) => e)
                                        } €
                                    </p>
                                    <p>Electricity: &nbsp;
                                        {energy_costs
                                            .filter((e) => (e.country === country) && (e.typology === 'Electricity'))
                                            .map((e) => { 
                                                var avg_cost = energy_costs.filter((e) => (e.country === 'AVG') && (e.typology === 'Electricity'))[0].price;
                                                return e.price === null ? avg_cost : e.price; 
                                            })
                                            .reduce((e) => e)
                                        } €
                                    </p>
                                </Col>
                            </Row>
                            <Card className='mt-3 mb-3'>
                                <Card.Body>
                                    <Card.Title>
                                        Reference Building
                                    </Card.Title>

                                    <Row>
                                        <Col xs={4} lg={2}>
                                            <Form.Group>
                                                <Form.Label>Reference Building</Form.Label>
                                                <Form.Control as="select"
                                                    id='reference'
                                                    custom
                                                    name="reference"
                                                    country={reference_buildings_details[reference_building_code].country}
                                                    code={reference_building_code}
                                                    onChange={(e) => setReference_building_code(e.target.value)}
                                                    defaultValue={reference_building_code}
                                                >
                                                    {Object.entries(reference_buildings_details).map(([val, index]) => {
                                                        return (<option key={val} value={val}>{reference_buildings_details[val].city}</option>);
                                                    })}
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={4} lg={2}>
                                            <Form.Group>
                                                <Form.Label>Spinned?</Form.Label>
                                                <Form.Check
                                                    className="text-center"
                                                    id='spinned'
                                                    label=""
                                                    custom
                                                    type="switch"
                                                    name="spinned"
                                                    checked={spinned}
                                                    onChange={(e) => setSpinned(e.target.checked)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col m={4} lg={4} style={is_client_pos ? {"display" : "none"} : {}}>
                                            <ReferenceBuildingDetails
                                                u={reference_buildings_details[reference_building_code].u}
                                                compactness={reference_buildings_details[reference_building_code].compactness}
                                                u_w={reference_buildings_details[reference_building_code].u_w}
                                                windows_per_wall_orientation={reference_buildings_details[reference_building_code].windows_per_wall_orientation}
                                            />
                                        </Col>
                                        <Col m={4} lg={4}>
                                            <Image className="pls_img" src={require('./assets/' + reference_building_code + (spinned ? '_spinned.png' : '.png'))} fluid />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                            <Row style={is_client_pos ? {"display" : "none"} : {}}>
                                <Col xs={4} lg={2}>
                                    <Form.Group>
                                        <Form.Label>Interest Rate</Form.Label>
                                        <Form.Control
                                            lang="en"
                                            step="any"
                                            name="interest_rate"
                                            onChange={(e) => setInterest_rate(e.target.value)}
                                            value={interest_rate}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4} lg={2}>
                                    <Form.Group>
                                        <Form.Label>Inflation</Form.Label>
                                        <Form.Control
                                            lang="en"
                                            step="any"
                                            name="inflation"
                                            onChange={(e) => setInflation(e.target.value)}
                                            value={inflation}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4} lg={2}>
                                    <Form.Group>
                                        <Form.Label>Years</Form.Label>
                                        <Form.Control
                                            lang="en"
                                            step="any"
                                            name="years"
                                            onChange={(e) => setYears(e.target.value)}
                                            value={years}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Container>
                    </Tab>

                    {
                        // Slabs tab
                    }

                    <Tab eventKey="slabs" title="Slabs" tabClassName={is_client_pos ? 'd-none' : ''}>
                        {renderSlabs()}
                    </Tab>

                    {
                        // Roofs tab
                    }

                    <Tab eventKey="roofs" title="Roofs" tabClassName={is_client_pos ? 'd-none' : ''}>
                        {renderRoofs()}
                    </Tab>

                    {
                        // Walls tab
                    }

                    <Tab eventKey="walls" title="Walls" tabClassName={is_client_pos ? 'd-none' : ''}>
                        {renderWalls()}
                    </Tab>

                    {
                        // Windows tab
                    }

                    <Tab eventKey="windows" title="Windows" tabClassName={is_client_pos ? 'd-none' : ''}>
                        {renderWindows()}
                    </Tab>

                    {
                        // Others tab
                    }

                    <Tab eventKey="others" title="Others" tabClassName={is_client_pos ? 'd-none' : ''}>
                        <Container fluid>
                            <Row>
                                <Col xs={12} lg={6}>{renderAirTightnesses()}</Col>
                                <Col xs={12} lg={6}>{renderVentImps()}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6}>{renderThermalBridges()}</Col>
                                <Col xs={12} lg={6}>{renderShadings()}</Col>
                            </Row>
                        </Container>
                    </Tab>

                    {
                        // Desired Systems tab
                    }

                    <Tab eventKey="desired_systems" title="Desired Systems">
                        <Container fluid>
                            <h2 className="text-center">Desired Systems</h2>
                            <Row>
                                <Col xs={6} lg={2}>
                                    <Form.Group>
                                        <Form.Label>Cooling combined with heating</Form.Label>
                                        <Form.Check
                                            className="text-center"
                                            id="desired_heat_both"
                                            label=""
                                            custom
                                            type="switch"
                                            name="desired_heat_both"
                                            checked={desired_heat_both}
                                            disabled={desired_combined_dhw}
                                            onChange={(e) => handleDesiredHeatBothChange(e)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={6} lg={2}>
                                    <Form.Group>
                                        <Form.Label>DHW combined with heating</Form.Label>
                                        <Form.Check
                                            className="text-center"
                                            id="desired_combined_dhw"
                                            label=""
                                            custom
                                            type="switch"
                                            name="desired_combined_dhw"
                                            checked={desired_combined_dhw}
                                            disabled={desired_heat_both}
                                            onChange={(e) => handleDesiredCombinedDHWChange(e)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Row>

                            </Form.Row>
                            <Form.Row>
                                <Col>
                                    <HVACSystem
                                        system_type="heating"
                                        system_state={desired_heating_system}
                                        changeFunc={handleDesiredHeatingSystemChange}
                                    />
                                </Col>
                                <Col xs={12} lg={4}>
                                    <DesiredSystemSelect
                                        system_type="heating"
                                        changeFunc={handleDesiredHeatingSystemSelectChange}
                                        selected_system={desired_heating_system_id}
                                        combined_dhw={desired_combined_dhw}
                                        heat_both={desired_heat_both}
                                    />
                                </Col>
                            </Form.Row>
                            <Form.Row>

                                <Col>
                                    <HVACSystem
                                        system_type="cooling"
                                        system_state={desired_cooling_system}
                                        disabled={desired_heat_both}
                                        changeFunc={handleDesiredCoolingSystemChange}
                                    />
                                </Col>
                                <Col xs={12} lg={4}>
                                    <DesiredSystemSelect
                                        system_type="cooling"
                                        changeFunc={handleDesiredCoolingSystemSelectChange}
                                        selected_system={desired_cooling_system_id}
                                        disabled={desired_heat_both}
                                        combined_dhw={desired_combined_dhw}
                                        heat_both={desired_heat_both}

                                    />
                                </Col>
                            </Form.Row>
                            <Form.Row>
                                <Col>
                                    <HVACSystem
                                        system_type="dhw"
                                        system_state={desired_dhw_system}
                                        disabled={desired_combined_dhw}
                                        changeFunc={handleDesiredDHWSystemChange}
                                    />
                                </Col>
                                <Col xs={12} lg={4}>
                                    <DesiredSystemSelect
                                        system_type="dhw"
                                        changeFunc={handleDesiredDHWSystemSelectChange}
                                        selected_system={desired_dhw_system_id}
                                        disabled={desired_combined_dhw}
                                        combined_dhw={desired_combined_dhw}
                                        heat_both={desired_heat_both}
                                    />
                                </Col>
                            </Form.Row>
                        </Container>
                    </Tab>

                    {
                        // Current Systems tab
                    }
                    <Tab eventKey="current_systems" title="Current Systems">
                        <Container fluid>
                        <h2 className="text-center">Current Systems</h2>
                            <Form.Row>
                                <Col xs={6} lg={2}>
                                    <Form.Group>
                                        <Form.Label>Cooling combined with heating</Form.Label>
                                        <Form.Check
                                            className="text-center"
                                            id="current_heat_both"
                                            label=""
                                            custom
                                            type="switch"
                                            name="current_heat_both"
                                            checked={current_heat_both}
                                            disabled={current_combined_dhw}
                                            onChange={(e) => handleCurrentHeatBothChange(e)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={6} lg={2}>
                                    <Form.Group>
                                        <Form.Label>DHW combined with heating</Form.Label>
                                        <Form.Check
                                            className="text-center"
                                            id="current_combined_dhw"
                                            label=""
                                            custom
                                            type="switch"
                                            name="current_combined_dhw"
                                            checked={current_combined_dhw}
                                            disabled={current_heat_both}
                                            onChange={(e) => handleCurrentCombinedDHWChange(e)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Form.Row>
                            <Form.Row>
                                <Col>
                                    <HVACSystem
                                        system_type="heating"
                                        system_state={current_heating_system}
                                        changeFunc={handleCurrentHeatingSystemChange}
                                    />
                                    <HVACSystem
                                        system_type="cooling"
                                        system_state={current_cooling_system}
                                        disabled={current_heat_both}
                                        changeFunc={handleCurrentCoolingSystemChange}
                                    />
                                    <HVACSystem
                                        system_type="dhw"
                                        system_state={current_dhw_system}
                                        disabled={current_combined_dhw}
                                        changeFunc={handleCurrentDHWSystemChange}
                                    />
                                </Col>
                            </Form.Row>

                        </Container>
                    </Tab>

                    {
                        // Solutions tab
                    }

                    <Tab eventKey="solutions"
                        title="Solutions"
                        disabled={disable_solutions_tab}>
                        {renderSolutions()}
                    </Tab>

                    <Tab eventKey="step_by_step"
                        title="Step by Step"
                        disabled={disable_solutions_tab}>
                        {renderStepByStep()}
                    </Tab>

                </Tabs>
            </Form>
            <Row>
                <Col xs={12} md={6}>
                    {computing_solutions ?
                        <Button className="computing-button" variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            &nbsp;Computing...
                        </Button> :
                        <Button className="computing-button" variant="primary" onClick={submitData}>Compute</Button>}
                </Col>
                <Col xs={12} md={6}>
                    <CopyToClipboard text={stateToJson()}>
                        <Button className="float-md-right copy-state-button" onClick={handleShowAlert}>Copy State to Clipboard</Button>
                    </CopyToClipboard>
                </Col>
            </Row>

        </Container >
    );

}

export default App;